/**
 * Retry and persistence policy for offline-replay mutations (#258).
 *
 * `main.tsx` registers these for every replayable def (`lib/offlineMutations.ts`)
 * so a queued write survives the two ways it used to be lost:
 * - a replay that hits a transient failure (flaky Wi-Fi, the 30s client
 *   timeout, a 429, a 502/503 mid-deploy) is retried with backoff; if it still
 *   fails it stays persisted, and comes back after a reload as a failed row in
 *   `<FailedMutationsSheet />` rather than disappearing;
 * - a write queued or in flight when the app is closed is persisted as queued
 *   and re-sent on the next launch.
 *
 * Re-sending is safe because every replayable request carries an idempotency
 * key, within the limits `services/api/idempotencySends.ts` enforces on every
 * send; its per-key record is persisted here on the mutation's `meta.replay`.
 */
import { hashKey, type Mutation } from '@tanstack/react-query'
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'
import { ApiError } from '@/services/api/ApiError'
import { clearKeyRefusal, getKeySend, seedKeySend, type KeySend } from '@/services/api/idempotencySends'
import { OfflineError } from '@/services/api/OfflineError'
import { SessionUnreachableError } from '@/services/api/SessionUnreachableError'
import { useAuthStore } from '@/store/authStore'

type DehydratedMutation = PersistedClient['clientState']['mutations'][number]
type MutationState = DehydratedMutation['state']
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- accepts any mutation; only key, state and meta are read
type AnyMutation = Pick<Mutation<any, any, any, any>, 'state' | 'options'>

/**
 * Persisted on the mutation's `meta`, so its key's send record survives a
 * reload. `key` names the idempotency key `send` belongs to: re-executing the
 * mutation with a fresh key ("Keep my change") must not inherit the old
 * key's first send.
 */
type ReplayMeta = { key?: string; send?: KeySend; failed?: boolean }

const MAX_TRANSIENT_RETRIES = 5

function idempotencyKeyOf(variables: unknown): string | undefined {
  const key = (variables as { idempotencyKey?: unknown } | null | undefined)?.idempotencyKey
  return typeof key === 'string' ? key : undefined
}

function replayMetaOf(meta: Record<string, unknown> | undefined): ReplayMeta | undefined {
  return meta?.replay as ReplayMeta | undefined
}

// The persisted record for `key`; for any other key, only its owner carries over.
function restoredSend(meta: Record<string, unknown> | undefined, key: string): KeySend | undefined {
  const replay = replayMetaOf(meta)
  if (!replay?.send) return undefined
  return replay.key === key ? replay.send : { userId: replay.send.userId }
}

/** Network failure (0), client timeout (408), rate limit (429) or a 5xx. */
export function isTransientError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  return error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500
}

/**
 * A queued write whose session couldn't be renewed keeps retrying until it can
 * be (#254), and so does one that failed because the device is offline but
 * the retryer doesn't know it (a cold start offline sends no `offline` event);
 * a transient failure is retried up to 5 times. TanStack counts every failure
 * of an attempt chain, so those retries use up that budget too. The retryer
 * pauses while the tab is hidden or known to be offline, so a backing-off
 * write stays queued.
 */
export function retryReplayable(failureCount: number, error: unknown): boolean {
  if (error instanceof SessionUnreachableError || error instanceof OfflineError) return true
  return isTransientError(error) && failureCount < MAX_TRANSIENT_RETRIES
}

// An offline failure takes TanStack offline (`main.tsx`), so its retry only
// needs to reach the retryer's pause, well inside the 30s offline re-check;
// waiting out the connection is `onlineManager`'s job.
const OFFLINE_RETRY_DELAY_MS = 1000

export function replayRetryDelay(attempt: number, error?: unknown): number {
  if (error instanceof OfflineError) return OFFLINE_RETRY_DELAY_MS
  return Math.min(1000 * 2 ** attempt, 30000)
}

/**
 * Pin a write's key to the user who made it as soon as it is created (a fresh
 * `pending`), or restore its send record when it is hydrated — so even its
 * first send can't go out as someone who signed in since. Called on
 * `continue` too: a write that starts an attempt (not paused) is no longer
 * refused, so the save that precedes the attempt records it as possibly sent.
 */
export function trackReplaySend(mutation: AnyMutation): void {
  const key = idempotencyKeyOf(mutation.state.variables)
  if (!key) return
  seedKeySend(key, restoredSend(mutation.options.meta, key) ?? { userId: useAuthStore.getState().user?.id })
  if (!mutation.state.isPaused) clearKeyRefusal(key)
}

/**
 * Persist a replayable mutation that hasn't landed: queued (paused), in flight
 * or backing off (pending), or failed transiently. A failure restored from
 * persistence (its error no longer an `ApiError` once cloned into IndexedDB)
 * stays persisted until it is retried or discarded. Anything without replay
 * defaults would hydrate without a mutationFn, so it is never persisted.
 */
export function shouldPersistMutation(mutation: AnyMutation, replayableKeyHashes: Set<string>): boolean {
  const { mutationKey, meta } = mutation.options
  if (mutationKey === undefined || !replayableKeyHashes.has(hashKey(mutationKey))) return false
  const { status, error } = mutation.state
  if (status === 'pending') return true
  if (status !== 'error') return false
  return error instanceof ApiError ? isTransientError(error) : replayMetaOf(meta)?.failed === true
}

// Running (not paused), or attempted before: the request may have left the
// device. Read from the state the cache saves BEFORE each attempt is sent, so
// the persisted record is never behind a request already in flight.
function mayHaveBeenSent(state: MutationState): boolean {
  return state.status === 'error' || !state.isPaused || state.failureCount > 0
}

/**
 * A live record (`beginKeySend` stamps it as each attempt goes out) is exact,
 * except in the save that runs as an attempt starts, before the request is
 * sent: a running write it shows unsent and not refused. A refused write
 * that is running is backing off, and the attempt it was refused stays unsent.
 * Without a live record, any sign of an attempt counts.
 */
function unsentAsSaved(live: KeySend | undefined, state: MutationState): boolean {
  if (!live) return !mayHaveBeenSent(state)
  return live.refused === true || state.status !== 'pending' || state.isPaused
}

function persistedSendFor(key: string, mutation: DehydratedMutation): KeySend | undefined {
  const live = getKeySend(key)
  const send = live ?? restoredSend(mutation.meta, key)
  if (send?.firstSentAt !== undefined || unsentAsSaved(live, mutation.state)) return send
  // Stamped no later than the send itself, so the replay window errs short.
  // Only the persisted copy: this save may run just before an attempt that
  // then waits on its scope or is refused, and only `beginKeySend` — the send
  // itself — records a first send in memory.
  return { userId: send ? send.userId : useAuthStore.getState().user?.id, firstSentAt: Date.now() }
}

/**
 * The persisted copy of a pending mutation is a queued one: paused. Hydrated
 * that way it resumes through `resumePausedMutations` (on restore or on
 * reconnect) instead of sitting pending forever and blocking every later write
 * in its scope (`MutationCache.canRun`). A failed one stays failed: later
 * writes in its scope may already have landed, and the user has been told it
 * failed, so it waits in the sheet for Retry or Discard. Only the copy is
 * rewritten — `dehydrate` hands over the live state object.
 */
function asPersisted(mutation: DehydratedMutation): DehydratedMutation {
  const { state } = mutation
  const key = idempotencyKeyOf(state.variables)
  const send = key && persistedSendFor(key, mutation)
  const failed = state.status === 'error'
  const replay: ReplayMeta = { ...(key && send && { key, send }), ...(failed && { failed }) }
  return {
    ...mutation,
    meta: { ...mutation.meta, replay },
    state: failed ? state : { ...state, isPaused: true, failureReason: null },
  }
}

/** Wraps a persister so the mutations it writes resume correctly on restore. */
export function persistMutationsForReplay(persister: Persister): Persister {
  return {
    ...persister,
    persistClient: (client) =>
      persister.persistClient({
        ...client,
        clientState: { ...client.clientState, mutations: client.clientState.mutations.map(asPersisted) },
      }),
  }
}
